import { createIntlTranslations } from 'Features/Intl/fns';
import { Footer } from 'Features/Shared/Components/Footer';
import { Navbar } from 'Features/Shared/Components/Navbar';
import { GetStaticPropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Meta } from '../src/components/atoms/meta/Meta';
import { HorizontalSlider } from '../src/components/molecules/horizontal-slider/HorizontalSlider';
import { usePixelViewContent } from '../src/core/facebook-pixel';
import { useGTMPageView } from '../src/core/gtm/effects/gtmPageView.effect';

interface ITimeline {
  title: string;
  desc: string;
}
export default function WhoWeAre() {
  // Hello
  const { t } = useTranslation(['chi-siamo']);
  const history: ITimeline[] = t('timeline');

  useGTMPageView(() => ({
    event: 'page_view',
    pageType: 'Marketing',
  }));
  usePixelViewContent(() => ({
    content_type: 'content',
  }));
  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto min-h-[calc(100vh-6rem)] px-6">
        <Meta {...t('metadata')} />
        <div className="main_page">
          <section className="desktop_text_block">
            <h1 className="desktop_text_block__title">{t('title')}</h1>
            <div className="desktop_text_block__subtitle">{t('subTitle')}</div>
            <p className="desktop_text_block__text">{t('text')}</p>
          </section>
          <hr />
          <section className="desktop_reason_why">
            <h2 className="desktop_reason_why__title">{t('whyTitle')}</h2>
            <ul className="desktop_reason_why__list">
              <li className="desktop_reason_why__list__item desktop_reason_why__list__item--fast">
                {t('whyTitle1')}
                <span>{t('whyDesc1')}</span>
              </li>
              <li className="desktop_reason_why__list__item desktop_reason_why__list__item--professional">
                {t('whyTitle2')}
                <span>{t('whyDesc2')}</span>
              </li>
              <li className="desktop_reason_why__list__item desktop_reason_why__list__item--secure">
                {t('whyTitle3')}
                <span>{t('whyDesc3')}</span>
              </li>
              <li className="desktop_reason_why__list__item desktop_reason_why__list__item--trasparent">
                {t('whyTitle4')}
                <span>{t('whyDesc4')}</span>
              </li>
            </ul>
          </section>
          <hr />
          <div className="static_template__info">
            <div className="static_template__info__container_left">
              <h2 className="static_template__info__container_left__title static_template__info__container_left__title--with_divider">
                {t('infoTitle')}
              </h2>
              <h3 className="static_template__info__container_left__sub_title">
                {t('infoSubTitle')}
              </h3>
            </div>
            <div className="static_template__info__container">
              <ul className="static_template__info__container__list ">
                <li className="static_template__info__container__list__item">
                  <div className="static_template__info__container__list__item__text">
                    <h2 className="static_template__info__container__title">{t('infoTitle1')}</h2>
                    {t('infoDesc1')}
                  </div>
                </li>
                <li className="static_template__info__container__list__item">
                  <div className="static_template__info__container__list__item__text">
                    <h2 className="static_template__info__container__title">{t('infoTitle2')}</h2>
                    {t('infoDesc2')}
                  </div>
                </li>
                <li className="static_template__info__container__list__item">
                  <div className="static_template__info__container__list__item__text">
                    <h2 className="static_template__info__container__title">{t('infoTitle3')}</h2>
                    {t('infoDesc3')}
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <hr />
          <HorizontalSlider
            totalItems={history.length}
            itemPerRowDesktop={3}
            containerClassName="timeline"
            listClassName="timeline__list"
            title={t('timeLineTitle')}
            startFromEnd={true}
            headerPadding={true}
          >
            {history.map((item: ITimeline, index: number) => {
              return (
                <div key={index} className="timeline__list__item">
                  <div className="timeline__list__item__middle">
                    {item.title}
                    <div className="timeline__list__item__middle__line"></div>
                    <div className="timeline__list__item__middle__image"></div>
                  </div>
                  <div className="timeline__list__item__content">
                    <span>{item.desc}</span>
                  </div>
                </div>
              );
            })}
          </HorizontalSlider>
          <hr />
          <section className="desktop_text_block">
            <h2 className="desktop_text_block__title">{t('foundersTitle')}</h2>
            <div className="desktop_text_block__subtitle">{t('foundersSubtitle')}</div>
          </section>
          <hr />
          <section className="desktop_text_block_50">
            <ul className="desktop_text_block_50__list">
              <li className="desktop_text_block_50__list__item">
                <img src="/assets/img/founders_dario_cardile.png" alt="Dario Cardile" />
                <h3 className="desktop_text_block_50__list__item__title">{t('name1')}</h3>
                <h4 className="desktop_text_block_50__list__item__subtitle">{t('job1')}</h4>
                <p className="desktop_text_block_50__list__item__text">{t('desc1')}</p>
              </li>
              <li className="desktop_text_block_50__list__item">
                <img src="/assets/img/founders_paolo_castelletti.png" alt="Paolo Castelletti" />
                <h3 className="desktop_text_block_50__list__item__title">{t('name2')}</h3>
                <h4 className="desktop_text_block_50__list__item__subtitle">{t('job2')}</h4>
                <p className="desktop_text_block_50__list__item__text">{t('desc2')}</p>
              </li>
            </ul>
          </section>
          <hr />
        </div>
      </div>
      <Footer />
    </>
  );
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const i18n = await serverSideTranslations(context.locale || 'it');
  const locale = context.locale?.startsWith('it') ? 'it' : 'en';
  const intl = await createIntlTranslations(locale);

  return {
    props: {
      ...i18n,
      ...intl,
    },
  };
}
